// Generic single-column settings hook. Mobile has no settings-context (each
// tool reads/writes user_settings directly — see e.g. KyrsTool), so each
// settings editor loads and persists its own column with this. Value is the
// exact JSONB/scalar shape the web app stores; see src/context/settings-
// context.tsx for the canonical column↔field mapping.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

export function useSettingColumn<T>(
  userId: string | undefined,
  column: string,
  fallback: T
): {
  value: T;
  setValue: (next: T) => void;
  loading: boolean;
  reload: () => void;
} {
  const [value, setValueState] = useState<T>(fallback);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase.from('user_settings').select(column).eq('user_id', userId).maybeSingle();
    const raw = (data as Record<string, unknown> | null)?.[column];
    setValueState(raw === undefined || raw === null ? fallback : (raw as T));
    setLoading(false);
    // fallback is intentionally excluded — it's a fresh literal each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, column]);

  useEffect(() => {
    void load();
  }, [load]);

  const setValue = useCallback(
    (next: T) => {
      setValueState(next);
      if (!userId) return;
      // supabase-js v2 builders are LAZY — a bare `void …update()` builds the
      // query but never sends it. Call `.then()` so the write actually fires.
      void supabase
        .from('user_settings')
        .update({ [column]: next })
        .eq('user_id', userId)
        .then(({ error }) => {
          if (error) console.warn(`[useSettingColumn] save failed for ${column}:`, error.message);
        });
    },
    [userId, column]
  );

  return { value, setValue, loading, reload: load };
}
